import { ReactNode, useCallback, useMemo } from "react";
import { Invoice } from "@common/types/invoice";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import {
    opensScheduleModalBeforeApply,
    resolveInvoiceTemplateBlockReason,
    SchedulePickerTemplateWithAnchors,
} from "@components/accounting/schedules/scheduleApplyConfirmHelpers";
import { useScheduleLifecycleRequests } from "@components/accounting/schedules/useScheduleLifecycleRequests";
import { useApplyInvoiceSchedule } from "./useApplyInvoiceSchedule";
import { useInvoiceScheduleCellChange } from "./useInvoiceScheduleCellChange";
import { useOpenInvoiceSchedule } from "./useOpenInvoiceSchedule";
import type { InvoiceDetailsScheduleCallbacks } from "./InvoiceDetailsSchedule";
import {
    isScheduleCellInteractive,
    resolveInvoiceScheduleCellState,
} from "./views/scheduleCellState";
import { resolveInvoiceScheduleReplaceScope } from "./views/invoiceScheduleReplaceScope";

/**
 * The apply, remove and lifecycle wiring an invoice schedule picker needs,
 * shared by the grid cell and the side panel so the two cannot drift. Each
 * surface supplies its own templates and renders the picker itself.
 */
export function useInvoiceSchedulePicker(options: {
    invoice: Invoice;
    callbacks: InvoiceDetailsScheduleCallbacks | undefined;
}) {
    const { invoice, callbacks } = options;
    const onRequestScheduleConfirm = callbacks?.onRequestScheduleConfirm;
    const onScheduleApplied = callbacks?.onScheduleApplied;
    const onCreateSchedule = callbacks?.onCreateSchedule;
    const {
        applySchedule: runApply,
        isApplying,
        linkedConflictModal,
    } = useApplyInvoiceSchedule({
        showLockedPeriods: callbacks?.showLockedPeriods,
    });

    const state = useMemo(
        () => resolveInvoiceScheduleCellState(invoice),
        [invoice],
    );
    const isInteractive = isScheduleCellInteractive(state);
    const onViewSchedule = useOpenInvoiceSchedule(state);

    const replaceScope = useMemo(
        () =>
            resolveInvoiceScheduleReplaceScope({
                invoice,
                scheduleApplicationId:
                    state.kind === "applied"
                        ? state.scheduleApplicationId
                        : null,
            }),
        [invoice, state],
    );

    const applySchedule = useCallback(
        async (
            template: SchedulePickerTemplateWithAnchors,
        ): Promise<boolean> => {
            if (opensScheduleModalBeforeApply(template)) {
                return (
                    (await onRequestScheduleConfirm?.({
                        invoice,
                        template,
                        ...replaceScope,
                        amount: Math.abs(replaceScope.amount),
                        sourceLabel: invoice.invoiceNumber ?? "Invoice",
                    })) ?? false
                );
            }
            return await runApply({
                templateId: template.id,
                target: {
                    entityId: invoice.entityId,
                    invoiceId: invoice.id,
                    ...replaceScope,
                },
                onApplied: onScheduleApplied,
            });
        },
        [
            invoice,
            replaceScope,
            onRequestScheduleConfirm,
            onScheduleApplied,
            runApply,
        ],
    );

    const { onSelect, onClear, confirmModal, isRemoving } =
        useInvoiceScheduleCellChange({
            entityId: invoice.entityId,
            state: isInteractive ? state : null,
            applySchedule,
            onScheduleChanged: onScheduleApplied,
            showLockedPeriods: callbacks?.showLockedPeriods,
        });

    const appliedState = state.kind === "applied" ? state : null;
    const { onChangeSchedule, onResumeSchedule } = useScheduleLifecycleRequests(
        {
            applicationId: appliedState?.scheduleApplicationId ?? null,
            status: appliedState?.status ?? null,
            entityId: invoice.entityId,
            sourceType: ScheduleSourceType.invoice,
            requestLifecycleAction: callbacks?.onRequestLifecycleAction,
        },
    );

    const overlays: ReactNode = (
        <>
            {confirmModal}
            {linkedConflictModal}
        </>
    );

    return {
        state,
        pickerProps: {
            isDisabled: isApplying || isRemoving,
            getBlockReason: resolveInvoiceTemplateBlockReason,
            onSelect,
            onClear,
            onChangeSchedule,
            onResumeSchedule,
            onViewSchedule,
            onCreate: onCreateSchedule
                ? () => onCreateSchedule({ entityId: invoice.entityId })
                : undefined,
        },
        overlays,
    };
}
